import type {
  ProjectAssignee,
  ProjectBoard,
  ProjectColumn,
  ProjectMember,
  ProjectTask,
  ProjectTaskCreateInput,
} from '../types/project';
import { formatMinutes, parseCalendarDateInput } from './dateTime';
import { isUnlimitedTaskLimit, projectMemberRoleLabel } from './projectForm';
import {
  completedDateFieldError,
  resolveCompletedDate,
  type CompletedOnChoice,
} from './taskCompletionDate';

const DURATION = /^(\d{1,3}):([0-5]\d)$/;

/** Matches the API / prj_subtask.subtask_name limit. */
export const SUBTASK_NAME_MAX_LENGTH = 2000;

// List markers pasted from documents, chats and markdown.
const BULLET_CHARS = '•●○◦▪▫■□‣⁃∙';
const LEADING_BULLET = new RegExp(`^(?:[-*+>–—](?:\\s+|$)|[${BULLET_CHARS}]\\s*)`);
const INLINE_BULLET = new RegExp(`\\s+[${BULLET_CHARS}]\\s+`);
// "1." "2)" "(3)" "a." "b)" "[ ]" "[x]" followed by whitespace.
const LEADING_NUMBER = /^(?:\(?\d{1,3}[.)\]:]|\(?[a-zA-Z][.)]|\[[ xX]?\])\s+/;

function stripListMarker(line: string, numbered: boolean): string {
  let text = line.trim();
  // Two passes cover combined markers such as "- [ ] item" or "• 1. item".
  for (let pass = 0; pass < 2; pass += 1) {
    text = text.replace(LEADING_BULLET, '');
    if (numbered) text = text.replace(LEADING_NUMBER, '');
    text = text.trim();
  }
  return text;
}

/**
 * Splits pasted or typed text into subtask points: one per line or bullet,
 * list markers removed, original wording and order kept, empty and repeated
 * (case-insensitive) points dropped. Numbering ("1.", "a)") is only stripped
 * when the text is a list, so a single "2. Fix login" is left as typed.
 */
function listPieces(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .flatMap(line => line.split(INLINE_BULLET))
    .map(line => line.trim())
    .filter(Boolean);
}

/** True when the text holds more than one line or bullet, even repeated ones. */
export function isSubtaskList(text: string): boolean {
  return listPieces(text).length > 1;
}

export function splitSubtaskPoints(text: string): string[] {
  const pieces = listPieces(text);
  const numbered = pieces.length > 1;
  const seen = new Set<string>();
  const points: string[] = [];
  for (const piece of pieces) {
    const point = stripListMarker(piece, numbered);
    const key = point.toLowerCase();
    if (!point || seen.has(key)) continue;
    seen.add(key);
    points.push(point);
  }
  return points;
}

/** Points not already used by another subtask on the task (case-insensitive). */
export function newSubtaskPoints(points: string[], existingNames: string[]): string[] {
  const taken = new Set(existingNames.map(name => name.trim().toLowerCase()));
  return points.filter(point => {
    const key = point.toLowerCase();
    if (taken.has(key)) return false;
    taken.add(key);
    return true;
  });
}

/**
 * Replaces a draft row whose text holds several points with one row per point,
 * placed where the original row was. New rows keep the row's members.
 */
export function expandSubtaskDraft(
  drafts: TaskSubtaskDraft[],
  key: string,
  text: string,
): TaskSubtaskDraft[] {
  const index = drafts.findIndex(item => item.key === key);
  if (index < 0) return drafts;
  if (!isSubtaskList(text)) {
    return drafts.map(item => (item.key === key ? { ...item, name: text } : item));
  }
  const others = drafts.filter(item => item.key !== key).map(item => item.name);
  const points = newSubtaskPoints(splitSubtaskPoints(text), others);
  const row = drafts[index];
  // Every point already exists elsewhere: leave the row empty rather than repeat it.
  if (!points.length) {
    return drafts.map(item => (item.key === key ? { ...item, name: '' } : item));
  }
  const rows = points.map((name, position) => ({
    ...row,
    key: position === 0 ? row.key : nextTaskDraftKey('subtask'),
    name,
  }));
  return [...drafts.slice(0, index), ...rows, ...drafts.slice(index + 1)];
}

/** A change that inserts more than one character with a line break is a paste. */
export function isMultiPointPaste(previous: string, next: string): boolean {
  return (
    next.length - previous.length > 1 &&
    /[\r\n]/.test(next) &&
    isSubtaskList(next)
  );
}

const BOARD_COLUMN_MIN_WIDTH = 260;
const BOARD_COLUMN_MAX_WIDTH = 360;

/**
 * Columns share the visible width between 260 and 360 px; once they no longer
 * fit at 260 px the board scrolls horizontally instead of squeezing them.
 */
export function boardColumnWidth(
  availableWidth: number,
  columnCount: number,
  gap: number,
): number {
  if (!(availableWidth > 0) || columnCount < 1) return BOARD_COLUMN_MIN_WIDTH;
  const fit = (availableWidth - gap * (columnCount - 1)) / columnCount;
  return Math.floor(
    Math.min(BOARD_COLUMN_MAX_WIDTH, Math.max(BOARD_COLUMN_MIN_WIDTH, fit)),
  );
}

/** Drops a deleted task from the board immediately, before it reloads. */
export function removeTaskFromBoard(board: ProjectBoard, taskId: number): ProjectBoard {
  return {
    ...board,
    columns: board.columns.map(column =>
      column.tasks.some(task => task.taskId === taskId)
        ? {
            ...column,
            tasks: column.tasks.filter(task => task.taskId !== taskId),
            taskCount: Math.max(0, column.taskCount - 1),
          }
        : column,
    ),
  };
}

export type TaskFormFieldErrors = {
  taskName?: string;
  member?: string;
  dueDate?: string;
  completedDate?: string;
  timeEstimate?: string;
  timeSpent?: string;
  subtasks?: string;
};

export type TaskSubtaskDraft = {
  key: string;
  name: string;
  assignedEmployeeIds: number[];
};

let taskDraftKey = 0;

export function nextTaskDraftKey(prefix: string): string {
  taskDraftKey += 1;
  return `${prefix}-${taskDraftKey}`;
}

export function columnRequiresTaskDetails(
  column: Pick<
    ProjectColumn,
    'projectColumnId' | 'isDefaultColumn' | 'isCompletedColumn' | 'displayOrder'
  >,
  columns: Array<
    Pick<
      ProjectColumn,
      'projectColumnId' | 'isDefaultColumn' | 'isCompletedColumn' | 'displayOrder'
    >
  >,
): boolean {
  const optional = [...columns]
    .filter(item => item.isDefaultColumn && !item.isCompletedColumn)
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        (left.projectColumnId ?? 0) - (right.projectColumnId ?? 0),
    )[0];
  if (!optional?.projectColumnId || !column.projectColumnId) return true;
  return column.projectColumnId !== optional.projectColumnId;
}

export function applyColumnTaskRules<T extends ProjectColumn>(columns: T[]): T[] {
  return columns.map(column => ({
    ...column,
    requiresTaskDetails: columnRequiresTaskDetails(column, columns),
  }));
}

export function parseDurationInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(DURATION);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function formatDurationInput(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function formatDurationCompact(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 1) return '';
  return formatMinutes(minutes);
}

export function isValidDurationInput(value: string, { allowEmpty = true } = {}): boolean {
  const trimmed = value.trim();
  if (!trimmed) return allowEmpty;
  return DURATION.test(trimmed);
}

export function memberOptionLabel(member: ProjectMember): string {
  return `${member.employeeName} - ${projectMemberRoleLabel(member.memberRole)}`;
}

export function assigneeSummary(
  assignees: ProjectAssignee[],
  fallbackName?: string | null,
): string | null {
  if (assignees.length) {
    return assignees.map(item => item.employeeName).join(', ');
  }
  return fallbackName?.trim() || null;
}

export function selectedMemberLabel(
  members: ProjectMember[],
  selectedIds: number[],
): string {
  if (!selectedIds.length) return 'Select';
  const names = selectedIds
    .map(id => members.find(member => member.employeeId === id)?.employeeName)
    .filter((name): name is string => Boolean(name));
  return names.length ? names.join(', ') : `${selectedIds.length} selected`;
}

export function toggleEmployeeId(selectedIds: number[], employeeId: number): number[] {
  return selectedIds.includes(employeeId)
    ? selectedIds.filter(id => id !== employeeId)
    : [...selectedIds, employeeId];
}

export function missingRequiredTaskDetails(input: {
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
}): string[] {
  const missing: string[] = [];
  const hasMember =
    (input.assignedEmployeeIds ?? []).some(id => id > 0) ||
    (input.assignedEmployeeId != null && input.assignedEmployeeId > 0);
  if (!hasMember) missing.push('Member');
  if (!parseCalendarDateInput(input.dueDate.trim())) missing.push('Due Date');
  const estimate = parseDurationInput(input.timeEstimate);
  if (estimate == null || estimate < 1) missing.push('Time Estimate');
  return missing;
}

export function requiredFieldsMessage(columnName: string, missing: string[]): string {
  return missing
    .map(field => `${field} is required for ${columnName} tasks.`)
    .join('\n');
}

export function moveBlockedMessage(columnName: string, missing: string[]): string {
  return `This task cannot be moved to ${columnName}.\n\nPlease add:\n${missing
    .map(field => `• ${field}`)
    .join('\n')}`;
}

export type TaskMovePlan =
  | { kind: 'noop' }
  | { kind: 'blocked'; title: string; message: string }
  | { kind: 'promptComplete' }
  | { kind: 'move' };

export function planTaskMove(input: {
  currentColumnId: number | null;
  column: {
    projectColumnId: number | null;
    columnName: string;
    taskCount: number;
    taskLimit: number | null;
    requiresTaskDetails: boolean;
    isCompletedColumn: boolean;
  };
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
}): TaskMovePlan {
  if (!input.column.projectColumnId || input.column.projectColumnId === input.currentColumnId) {
    return { kind: 'noop' };
  }
  if (
    !isUnlimitedTaskLimit(input.column.taskLimit) &&
    input.column.taskLimit != null &&
    input.column.taskCount >= input.column.taskLimit
  ) {
    return {
      kind: 'blocked',
      title: 'Task limit reached',
      message: `${input.column.columnName} already has ${input.column.taskCount} / ${input.column.taskLimit} tasks.`,
    };
  }
  if (input.column.isCompletedColumn) {
    return { kind: 'promptComplete' };
  }
  if (input.column.requiresTaskDetails) {
    const missing = missingRequiredTaskDetails({
      assignedEmployeeId: input.assignedEmployeeId,
      assignedEmployeeIds: input.assignedEmployeeIds,
      dueDate: input.dueDate,
      timeEstimate: input.timeEstimate,
    });
    if (missing.length) {
      return {
        kind: 'blocked',
        title: 'Cannot move task',
        message: moveBlockedMessage(input.column.columnName, missing),
      };
    }
  }
  return { kind: 'move' };
}

/** Optimistically move a task into another board column without waiting on the API. */
export function relocateTaskOnBoard(
  board: ProjectBoard,
  taskId: number,
  targetColumnId: number,
): ProjectBoard {
  let moved: ProjectTask | null = null;
  const withoutTask = board.columns.map(column => {
    const index = column.tasks.findIndex(task => task.taskId === taskId);
    if (index < 0) return column;
    moved = {
      ...column.tasks[index],
      projectColumnId: targetColumnId,
    };
    return {
      ...column,
      tasks: column.tasks.filter(task => task.taskId !== taskId),
      taskCount: Math.max(0, column.taskCount - 1),
    };
  });
  if (!moved) return board;
  const relocated = moved;
  return {
    ...board,
    columns: withoutTask.map(column => {
      if (column.projectColumnId !== targetColumnId) return column;
      if (column.tasks.some(task => task.taskId === taskId)) return column;
      return {
        ...column,
        tasks: [...column.tasks, relocated],
        taskCount: column.taskCount + 1,
      };
    }),
  };
}

export function validateCreateTaskForm(input: {
  taskName: string;
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate: string;
  timeEstimate: string;
  /** Omitted by Create Task: new tasks start at zero and gain time via time logs. */
  timeSpent?: string;
  description: string;
  descriptionOpen: boolean;
  subtasks: TaskSubtaskDraft[];
  requiresTaskDetails: boolean;
  requireTimeSpent?: boolean;
  /** When creating directly in a completed column. */
  requireCompletedDate?: boolean;
  completedOn?: CompletedOnChoice;
  customCompletedDate?: string;
  columnName: string;
}): { error: string | null; fieldErrors: TaskFormFieldErrors; payload: ProjectTaskCreateInput | null } {
  const fieldErrors: TaskFormFieldErrors = {};
  const taskName = input.taskName.trim();
  if (!taskName) fieldErrors.taskName = 'Task name is required.';

  const assignedEmployeeIds = [
    ...new Set(
      (input.assignedEmployeeIds ??
        (input.assignedEmployeeId != null ? [input.assignedEmployeeId] : [])
      ).filter(id => id > 0),
    ),
  ];

  const dueDate = input.dueDate.trim();
  if (dueDate && !parseCalendarDateInput(dueDate)) {
    fieldErrors.dueDate = 'Due date is not a valid date.';
  }

  if (!isValidDurationInput(input.timeEstimate)) {
    fieldErrors.timeEstimate = 'Time estimate must use hh:mm format.';
  }
  if (input.timeSpent !== undefined && !isValidDurationInput(input.timeSpent)) {
    fieldErrors.timeSpent = 'Time spent must use HH:mm format.';
  }

  // A row still holding several typed points becomes one subtask per point.
  const namedSubtasks = input.subtasks.flatMap(item =>
    isSubtaskList(item.name)
      ? splitSubtaskPoints(item.name).map(name => ({ ...item, name }))
      : [{ ...item, name: item.name.trim() }],
  );
  const filled = namedSubtasks.filter(item => item.name);
  if (namedSubtasks.some(item => !item.name) && namedSubtasks.length > 0) {
    fieldErrors.subtasks = 'Subtask name cannot be empty.';
  }
  const unique = new Set(filled.map(item => item.name.toLowerCase()));
  if (unique.size !== filled.length) {
    fieldErrors.subtasks = 'Subtask names must be unique.';
  }
  if (filled.some(item => item.name.length > SUBTASK_NAME_MAX_LENGTH)) {
    fieldErrors.subtasks = `Subtask must not exceed ${SUBTASK_NAME_MAX_LENGTH} characters.`;
  }

  if (input.requiresTaskDetails) {
    const missing = missingRequiredTaskDetails({
      assignedEmployeeIds,
      dueDate,
      timeEstimate: input.timeEstimate,
    });
    if (missing.includes('Member')) {
      fieldErrors.member = `Member is required for ${input.columnName} tasks.`;
    }
    if (missing.includes('Due Date')) {
      fieldErrors.dueDate = `Due Date is required for ${input.columnName} tasks.`;
    }
    if (missing.includes('Time Estimate')) {
      fieldErrors.timeEstimate = `Time Estimate is required for ${input.columnName} tasks.`;
    }
  }

  if (input.requireTimeSpent) {
    const spentMinutes = parseDurationInput(input.timeSpent ?? '');
    if (
      (spentMinutes == null || spentMinutes < 1) &&
      !fieldErrors.timeSpent
    ) {
      fieldErrors.timeSpent = `Time Spent is required for ${input.columnName} tasks.`;
    }
  }

  let completedDate: string | null = null;
  if (input.requireCompletedDate) {
    const completedOn = input.completedOn ?? 'today';
    const customCompletedDate = input.customCompletedDate ?? '';
    const dateError = completedDateFieldError(completedOn, customCompletedDate);
    if (dateError) {
      fieldErrors.completedDate = dateError;
    } else {
      completedDate = resolveCompletedDate(completedOn, customCompletedDate);
    }
  }

  const error =
    fieldErrors.taskName ||
    fieldErrors.member ||
    fieldErrors.dueDate ||
    fieldErrors.completedDate ||
    fieldErrors.timeEstimate ||
    fieldErrors.timeSpent ||
    fieldErrors.subtasks ||
    null;
  if (error) return { error, fieldErrors, payload: null };

  const estimate = parseDurationInput(input.timeEstimate);
  const spent =
    input.timeSpent === undefined ? undefined : parseDurationInput(input.timeSpent);
  const description = input.descriptionOpen ? input.description.trim() : '';

  return {
    error: null,
    fieldErrors,
    payload: {
      projectColumnId: 0,
      taskName,
      assignedEmployeeId: assignedEmployeeIds[0] ?? null,
      assignedEmployeeIds,
      dueDate: dueDate || null,
      ...(input.requireCompletedDate ? { completedDate } : {}),
      timeEstimate: estimate && estimate > 0 ? formatDurationInput(estimate) : null,
      ...(spent === undefined
        ? {}
        : { timeSpent: formatDurationInput(spent ?? 0) || '00:00' }),
      description: description || null,
      subtasks: filled.map(item => ({
        subtaskName: item.name,
        assignedEmployeeIds: item.assignedEmployeeIds ?? [],
      })),
    },
  };
}
